import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { LGoCard, LGoCardHeader, LGoCardTitle, LGoCardDescription } from "@/ui/branded";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/ui/primitives/collapsible";
import { cn } from "@/lib/cn";
import { formatDateTime } from "@/lib/time";
import { tradingCapabilities, type PaymentAccount } from "@/domain/payment-account/types";
import { capabilityLabels } from "./payment-account-copy";

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <dt className="text-sm text-muted-foreground">{label}</dt>
      <dd className="font-mono text-sm text-foreground break-all">{value}</dd>
    </div>
  );
}

/** What someone answering a partner's phone call needs: Stripe's own words,
 *  unsummarised. Czech labels the meaning where it is known, the raw field
 *  beside it where it is not. */
export function PaymentAccountDiagnostics({ account }: { account: PaymentAccount }) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <LGoCard variant="flat" className="bg-muted/40">
      <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <LGoCardHeader className="mb-0">
          <LGoCardTitle>
            <CollapsibleTrigger className="flex w-full items-center justify-between gap-2 text-left">
              Technické údaje o účtu
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                  isOpen && "rotate-180",
                )}
                aria-hidden="true"
              />
            </CollapsibleTrigger>
          </LGoCardTitle>
          <LGoCardDescription>
            Odpověď Stripe tak, jak přišla. Když voláte na podporu, přečtěte odsud.
          </LGoCardDescription>
        </LGoCardHeader>

        <CollapsibleContent className="mt-4">
          <dl className="space-y-1">
            <Field label="ID účtu" value={account.accountId ?? "—"} />
            <Field label="Může prodávat" value={account.canTrade ? "true" : "false"} />
            <Field
              label="Naposledy načteno ze Stripe"
              value={account.syncedAt ? formatDateTime(account.syncedAt) : "nikdy"}
            />
            <Field label="Chyba načtení" value={account.syncError ?? "—"} />
          </dl>

          <div className="mt-4 space-y-2">
            {tradingCapabilities.map((name) => {
              const capability = account.capabilities[name];
              return (
                <div key={name}>
                  <Field label={capabilityLabels[name]} value={capability.status ?? "null"} />
                  {capability.statusDetails.map((detail, index) => (
                    <p
                      key={index}
                      className="pl-4 font-mono text-xs text-muted-foreground break-all"
                    >
                      {detail.code ?? "—"} · {detail.resolution ?? "—"}
                    </p>
                  ))}
                </div>
              );
            })}
          </div>

          <div className="mt-4">
            <p className="text-sm text-muted-foreground">
              Požadavky Stripe ({account.requirements.length})
            </p>
            {account.requirements.map((requirement, index) => (
              <div key={index} className="mt-2 border-l-2 border-border pl-3">
                <p className="font-mono text-sm text-foreground break-all">
                  {requirement.description}
                </p>
                <p className="font-mono text-xs text-muted-foreground">
                  čeká na: {requirement.awaitingActionFrom ?? "—"} · termín:{" "}
                  {requirement.deadlineStatus ?? "—"}
                </p>
                {requirement.restrictsCapabilities.length > 0 && (
                  <p className="font-mono text-xs text-muted-foreground break-all">
                    blokuje:{" "}
                    {requirement.restrictsCapabilities
                      .map((restricted) =>
                        [restricted.capability, restricted.configuration]
                          .filter(Boolean)
                          .join(" / "),
                      )
                      .join(", ")}
                  </p>
                )}
                {requirement.errors.map((error, errorIndex) => (
                  <p key={errorIndex} className="font-mono text-xs text-destructive break-all">
                    {error.code ?? "—"}: {error.description ?? "—"}
                  </p>
                ))}
              </div>
            ))}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </LGoCard>
  );
}
